import { useState } from 'react';
import clsx from 'clsx';

interface BulkConfirmDialogProps {
  title: string;
  description: string;
  items: Array<{ id: string; label: string }>;
  confirmLabel?: string;
  variant?: 'default' | 'danger';
  confirmDisabled?: boolean;
  onConfirm: () => Promise<{ succeeded: number; failed: number } | void>;
  onClose: () => void;
  children?: React.ReactNode;
}

export default function BulkConfirmDialog({
  title,
  description,
  items,
  confirmLabel = 'Confirm',
  variant = 'default',
  confirmDisabled = false,
  onConfirm,
  onClose,
  children,
}: BulkConfirmDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<{ succeeded: number; failed: number } | null>(null);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      const res = await onConfirm();
      if (res) {
        setResult(res);
      } else {
        onClose();
      }
    } catch (err) {
      setResult({ succeeded: 0, failed: items.length });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>

        <div className="px-6 py-4">
          {/* Optional extra content (dropdowns, selects, etc.) */}
          {children && <div className="mb-4">{children}</div>}

          {/* Items list */}
          <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
            {items.map((item) => (
              <div
                key={item.id}
                className="px-3 py-2 text-sm text-gray-700 border-b border-gray-100 last:border-b-0"
              >
                {item.label}
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-2">{items.length} item{items.length !== 1 ? 's' : ''}</p>

          {/* Result */}
          {result && (
            <div className={clsx(
              'mt-4 p-3 rounded-lg text-sm',
              result.failed === 0
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-amber-50 border border-amber-200 text-amber-700'
            )}>
              {result.succeeded} succeeded, {result.failed} failed
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          {result ? (
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium"
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={handleConfirm}
                disabled={isLoading || confirmDisabled}
                className={clsx(
                  'flex-1 px-4 py-2 rounded-lg font-medium disabled:opacity-50',
                  variant === 'danger'
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                )}
              >
                {isLoading ? 'Processing...' : confirmLabel}
              </button>
              <button
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium disabled:opacity-50"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
